import { Component, HostListener, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { NgSelectComponent } from "@ng-select/ng-select";
import { NgbCustomParserFormatter } from "components/common/date-picker/ngb-custom-parser-formatter";
import { AlertController, NavController, NavParams } from "ionic-angular";
import * as moment from "moment-timezone";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, take } from "rxjs/operators";
import { AuthService } from "../../../app/auth/auth.service";
import Activity from "../../../models/company-resources/activity";
import ContentWrapper from "../../../models/content-wrapper/content-wrapper";
import { MessagePatient } from "../../../models/messaging/channel";
import { WorkforceActivityItem } from "../../../models/system-admin/workforce-activity-item";
import { WorkforceContact } from "../../../models/system-admin/workforce-contact";
import { PatientChartService } from "../../../providers/patient-charts/patient-chart.service";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";
import { SystemAdminService } from "../../../providers/system-admin/system-admin.service";

interface ActivityTime {
  hour: number;
  minute: number;
}

@Component({
  selector: "activity-log-add",
  templateUrl: "activity-log-add.html",
  providers: [
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
})
export class ActivityLogAddComponent implements OnInit {
  userSearchSubject: Subject<string> = new Subject();
  patientSearchSubject: Subject<string> = new Subject();
  searchedPatients: MessagePatient[] = [];
  activity: Activity;
  workforceActivityItem = {} as WorkforceActivityItem;
  activityLogForm: FormGroup;
  subscriptions: Subscription[] = [];
  hours = [];
  mins: number[] = [0, 15, 30, 45];
  patientRequiredActivity = [
    1, 6, 9, 10, 11, 12, 13, 14, 15, 16, 17, 19, 21, 25, 26, 27, 35, 36, 37, 38,
    39, 40, 41, 42, 43, 44, 45, 47, 49, 50, 51, 52, 53, 54, 56, 57, 61, 63, 65,
    66, 67, 68,
  ];
  patientRequired: boolean = true;
  dateOfActivityMin: any = "";
  dateOfActivityMax: any = "";

  durationString: string = "0 hr 0 mins";
  totalTimeString: string = "0 hr 0 mins";
  @ViewChild("userListSelect") userListSelect: NgSelectComponent;
  @ViewChild("hiddenInput") hiddenInput: {
    setFocus: () => void;
  };

  initTimeValue: ActivityTime = {
    hour: null,
    minute: null,
  };
  now = new Date();
  today = {
    year: this.now.getFullYear(),
    month: this.now.getMonth(),
    day: this.now.getDate(),
  };

  initStartTime: ActivityTime = this.initTimeValue;
  initEndTime: ActivityTime = this.initTimeValue;

  activityTypeOptions = [];
  userOptions: WorkforceContact[] = [];
  activityTitle: string = "Add New Activity";
  selectedUser: WorkforceContact = null;
  selectedPatient: MessagePatient = null;
  selectedActivity: any = "";
  employeeActivities: WorkforceActivityItem[] = [];
  editMode = false;

  constructor(
    protected formBuilder: FormBuilder,
    public navCtrl: NavController,
    private navParams: NavParams,
    private alertController: AlertController,
    private resourceManagementService: ResourceManagementService,
    private patientChartService: PatientChartService,
    private authSvc: AuthService,
    private systemAdminService: SystemAdminService
  ) {
    this.dateOfActivityMin = {
      year: this.now.getFullYear() - 80,
      month: this.now.getMonth() + 1,
      day: this.now.getDate(),
    };
    this.dateOfActivityMax = {
      year: this.now.getFullYear(),
      month: this.now.getMonth() + 1,
      day: this.now.getDate(),
    };

    this.activityLogForm = this.formBuilder.group({
      activityType: ["", [Validators.required]],
      addAnotherActivity: [false],
      dateOfActivity: [this.today, Validators.required],
      endTime: [null, Validators.required],
      isHoliday: [false],
      mileage: [null],
      isOnCall: [false],
      patientChartId: [null, Validators.required],
      startTime: [null, [Validators.required]],
      timeZone: moment.tz.guess(),
      travelHour: [null],
      travelMinute: [null],
      travelTime: [Validators.required],
      userId: [null, [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.getActivitiesTypes();
    this.subscribeUserSearch();
    this.subscribePatientSearch();
    this.searchUsers("");

    this.hours = Array.from(Array(24).keys()).map((x) => {
      return {
        display: x,
        value: x,
      };
    });

    this.subscriptions.push(
      this.activityLogForm.valueChanges.subscribe((value) => {
        if (value.activityType != null) {
          this.patientRequiredCheck();
        }
        sessionStorage.setItem("activityLogForm", JSON.stringify(value));
        this.updateTime();
      })
    );
  }

  initActivity(reset: boolean = false) {
    if (this.navParams.get("activity")) {
      this.workforceActivityItem = this.navParams.get("activity");
    }
    if (this.workforceActivityItem.activityId && !reset) {
      let startDate = moment(
        this.workforceActivityItem.activityStartDatetime
      ).format("YYYY-MM-DD");
      let startHour = moment(
        this.workforceActivityItem.activityStartDatetime
      ).format("HH");
      let startMinute = moment(
        this.workforceActivityItem.activityStartDatetime
      ).format("mm");
      let endHour = moment(
        this.workforceActivityItem.activityEndDatetime
      ).format("HH");
      let endMinute = moment(
        this.workforceActivityItem.activityEndDatetime
      ).format("mm");
      let travelHours = moment
        .utc(this.workforceActivityItem.travelTime * 60000)
        .format("HH"); // travel time is in minutes.  * 60000 (ms)
      let travelMinutes = moment
        .utc(this.workforceActivityItem.travelTime * 60000)
        .format("mm"); // travel time is in minutes.  * 60000 (ms)
      this.activityTitle = "Edit Activity";
      this.editMode = true;
      let st = {
        hour: startHour,
        minute: startMinute,
      };

      let et = {
        hour: endHour,
        minute: endMinute,
      };

      this.mapFormFromActivity(
        this.workforceActivityItem,
        startDate,
        st,
        et,
        travelHours,
        travelMinutes
      );
    } else {
      this.activity = new Activity();

      if (this.navParams.get("userDetails")) {
        let activity: WorkforceActivityItem = this.navParams.get("userDetails");
        this.selectUser(activity);
      }

      let st = {
        hour: null,
        minute: null,
      };
      let startDate = moment
        .tz(new Date(), moment.tz.guess())
        .format("YYYY-MM-DD");
      this.mapFormFromActivity(
        this.workforceActivityItem,
        new Date(startDate),
        st,
        st,
        "0",
        "0"
      );
      this.activityLogForm.controls["dateOfActivity"].setValue(this.today, {
        emitEvent: false,
      });
      this.activityLogForm.controls["timeZone"].setValue(moment.tz.guess(), {
        emitEvent: false,
      });
    }
  }

  @HostListener("window:popstate", ["$event"])
  dismissModal() {
    this.navCtrl.pop();
  }

  private getActivitiesTypes() {
    this.resourceManagementService
      .getLookUpValues()
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.activityTypeOptions = response["ActivityType"].map((item) => {
            return {
              display: item.description,
              value: item.id,
            };
          });
          this.initActivity();
        },
        (err) => {}
      );
  }

  private subscribePatientSearch() {
    this.patientSearchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((x: string) => {
        if (!x || x.length === 0) {
          this.searchedPatients = [];
        } else if (x.length > 2) {
          this.searchPatients(x);
        }
      });
  }

  private subscribeUserSearch() {
    this.userSearchSubject
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe((x: string) => {
        if (!x || x.length === 0) {
          this.userOptions = [];
        } else if (x.length > 2) {
          this.searchUsers(x);
        }
      });
  }

  patientRequiredCheck() {
    this.patientRequired = this.patientRequiredActivity.includes(
      parseInt(this.activityLogForm.get("activityType").value.value)
    );
    if (this.patientRequired) {
      this.addPatientField();
    } else {
      this.removePatientField();
    }
  }

  addPatientField() {
    if (!this.activityLogForm.contains("patientChartId")) {
      this.activityLogForm.addControl(
        "patientChartId",
        this.formBuilder.control(null, Validators.required)
      );
    }
  }

  removePatientField() {
    if (this.activityLogForm.contains("patientChartId")) {
      this.activityLogForm.removeControl("patientChartId");
    }
  }

  changeTime($event, field) {
    if ($event != null && $event != "null") {
      let time: string = $event;
      this.activityLogForm.get(field).setValue(JSON.parse(time));
    }
  }

  updateTime() {
    let startTime = this.activityLogForm.get("startTime").value;
    let endTime = this.activityLogForm.get("endTime").value;
    const start = moment.duration({
      hour: startTime ? startTime.hour : 0,
      minute: startTime ? startTime.minute : 0,
    });
    const end = moment.duration({
      hour: endTime ? endTime.hour : 0,
      minute: endTime ? endTime.minute : 0,
    });
    const travel = moment.duration(this.travelTimeInSeconds, "seconds");

    let duration = end.subtract(start);

    if (!(duration.hours() >= 0 && duration.minutes() >= 0)) {
      let overNight = moment.duration({ hour: 24, minute: 0 });
      duration = duration.add(overNight);
    }

    this.durationString = moment
      .utc(duration.asMilliseconds())
      .format("H [hr] m [mins]");
    let total = travel.add(duration);
    this.totalTimeString = moment
      .utc(total.asMilliseconds())
      .format("H [hr] m [mins]");
    if (total.days()) {
      this.totalTimeString =
        24 +
        +this.totalTimeString.substring(0, 2) +
        this.totalTimeString.substring(2);
    }
  }

  submit() {
    /* Check for Add another option selected if yes we do not see submit confirmation */
    if (this.activityLogForm.value.addAnotherActivity) {
      let employeeActivityItem = this.mapActivityFromForm(
        this.activityLogForm.value,
        true
      );
      this.addEmployeeActivityItem(employeeActivityItem);
      this.resetAddActivityForm();
    } else {
      let alert = this.alertController.create({
        title: "Submit Confirmation",
        message: "Are you sure you wish to save this activity?",
        buttons: [
          {
            text: "No",
            handler: () => {},
          },
          {
            text: "Yes",
            handler: () => {
              let employeeActivityItem = this.mapActivityFromForm(
                this.activityLogForm.value,
                true
              );
              if (this.editMode && employeeActivityItem.activityId) {
                this.employeeActivities = new Array(employeeActivityItem);
                this.saveEmployeeActivities();
              } else if (!this.editMode) {
                this.addEmployeeActivityItem(employeeActivityItem);
                this.saveEmployeeActivities();
              }
            },
          },
        ],
      });
      alert.present();
    }
  }

  private resetAddActivityForm() {
    this.activityLogForm.reset({ emitEvent: true });
    this.searchedPatients = [];
    this.userOptions = [];
    this.workforceActivityItem = {} as WorkforceActivityItem;
    this.initActivity(true);
    this.patientRequired = false;
    this.searchUsers("");
  }

  addEmployeeActivityItem(workforceActivityItem: WorkforceActivityItem) {
    this.employeeActivities.push(workforceActivityItem);
  }

  private saveEmployeeActivities() {
    this.systemAdminService
      .createActivity(this.employeeActivities)
      .pipe()
      .take(1)
      .subscribe(
        (response) => {
          if (response && response["length"] > 0) {
            let date: WorkforceActivityItem = response[0],
              message: string,
              title: string;
            if (this.editMode) {
              title = "Activity Edited";
              message = `You have successfully edited ${
                date.activityType
              } activity for ${
                date.userLastName && date.userFirstName
                  ? date.userLastName + ", " + date.userFirstName
                  : date.userName
                  ? date.userName
                  : ""
              }.`;
            } else {
              title = "Activity Saved";
              if (this.employeeActivities.length > 1) {
                message =
                  `You have successfully saved ` +
                  this.employeeActivities.length +
                  ` Activities`;
              } else {
                message = `You have successfully saved ${
                  date.activityType
                } activity for ${
                  date.userLastName && date.userFirstName
                    ? date.userLastName + "," + date.userFirstName
                    : date.userName
                    ? date.userName
                    : ""
                }.`;
              }
            }
            this.showConfirmationMessage(title, message);
          }
          this.employeeActivities = [];
        },
        (err) => {
          this.employeeActivities = [];
          this.showConfirmationMessage(
            "Error",
            `Error while ${this.editMode ? "editing" : "saving"}  activity.`
          );
        }
      );
  }

  showConfirmationMessage(title: string, message: string): void {
    let alert = this.alertController.create({
      title: title,
      message: message,
      buttons: [
        {
          text: "OK",
        },
      ],
    });
    alert.present();
    alert.onDidDismiss(() => {
      this.navCtrl.pop();
    });
  }

  mapFormFromActivity(
    activity: WorkforceActivityItem,
    activityDate: Date,
    startTime: ActivityTime,
    endTime: ActivityTime,
    travelHours: string,
    travelMinutes: string
  ): void {
    let activityType = this.activityTypeOptions.find(
      (a) => a.display === this.workforceActivityItem.activityType
    );
    //this.selectedActivity = activityType;
    this.activityLogForm.controls["activityType"].setValue(activityType);
    this.activityLogForm.controls["mileage"].setValue(activity.mileage, {
      emitEvent: false,
    });
    this.activityLogForm.controls["isHoliday"].setValue(activity.isHoliday, {
      emitEvent: false,
    });
    this.activityLogForm.controls["isOnCall"].setValue(activity.isOnCall, {
      emitEvent: false,
    });
    this.initStartTime = startTime;
    this.initEndTime = endTime;

    if (!activity.activityId) {
      return;
    }

    this.activityLogForm.controls["dateOfActivity"].setValue(activityDate, {
      emitEvent: false,
    });
    this.activityLogForm.controls["startTime"].setValue(startTime, {
      emitEvent: false,
    });
    this.activityLogForm.controls["endTime"].setValue(endTime, {
      emitEvent: false,
    });
    this.activityLogForm.controls["travelHour"].setValue(
      parseInt(travelHours),
      { emitEvent: true }
    );
    this.activityLogForm.controls["travelMinute"].setValue(
      parseInt(travelMinutes),
      { emitEvent: true }
    );

    let patientOption: MessagePatient = {
      firstName: activity.patientFirstName,
      id: activity.patientChartId,
      lastName: activity.patientLastName,
      displayName: `${activity.patientLastName}, ${activity.patientFirstName} - ${activity.patientChartId}`,
      internalId: activity.patientChartId,
      typeOfCare: "Type Of Care",
      //activity.PatientCareType
    };

    if (this.patientRequired) {
      this.activityLogForm.controls["patientChartId"].setValue(patientOption, {
        emitEvent: false,
      });
      this.selectedPatient = patientOption;
    }

    this.searchedPatients.push(patientOption);
    this.selectUser(activity);
    this.updateTime();
  }

  selectUser(activity: WorkforceActivityItem) {
    const middleInitial = activity.userMiddleInitial
      ? activity.userMiddleInitial
      : "";
    const userFirstName = activity.userFirstName ? activity.userFirstName : "";
    const userLastName = activity.userLastName ? activity.userLastName : "";
    const userName = activity.userName
      ? activity.userName
      : `${userLastName}, ${userFirstName} ${middleInitial}`.trim();
    let userOption: WorkforceContact = {
      contactType: "0",
      displayName: `${userName} - ${activity.userId}`,
      firstName: userFirstName,
      id: +activity.userId,
      externalId: +activity.userId,
      lastName: userLastName,
      middleInitial: middleInitial,
      userName: userName,
    };

    this.activityLogForm.controls["userId"].setValue(userOption, {
      emitEvent: false,
    });
    this.selectedUser = userOption;
    this.userOptions.push(userOption);
  }

  mapActivityFromForm(
    form: any,
    editMode: boolean = false
  ): WorkforceActivityItem {
    let employeeActivityItem = {} as WorkforceActivityItem;
    if (editMode) {
      employeeActivityItem = this.workforceActivityItem;
    }
    const user = this.authSvc.getUser();
    const orgID = user.tenantId;
    const userID = user.userId;
    if (form.startTime !== null) {
      employeeActivityItem.activityStartDatetime = new Date(
        moment(form.dateOfActivity)
          .hour(form.startTime.hour)
          .minute(form.startTime.minute)
          .format()
      ).toISOString();
    } else {
      employeeActivityItem.activityStartDatetime = new Date(
        moment(form.dateOfActivity).hour(0).minute(0).format()
      ).toISOString();
    }
    if (form.endTime !== null) {
      employeeActivityItem.activityEndDatetime = new Date(
        moment(form.dateOfActivity)
          .hour(form.endTime.hour)
          .minute(form.endTime.minute)
          .format()
      ).toISOString();
    }
    let activityType = this.selectedActivity.display;
    if (this.selectedActivity.display === "In-Patient Visit")
      activityType = "In-Patient";
    else if (this.selectedActivity.display === "Even More Care (EMC)")
      activityType = "EMC";
    employeeActivityItem.activityType = activityType;
    employeeActivityItem.createdBy = userID.toString();
    employeeActivityItem.discipline = "";
    employeeActivityItem.isHoliday = form.isHoliday;
    employeeActivityItem.isOnCall = form.isOnCall;
    employeeActivityItem.mileage = Number(form.mileage);
    if (this.patientRequired) {
      employeeActivityItem.patientChartId = this.selectedPatient.id;
      employeeActivityItem.patientFirstName = this.selectedPatient.firstName;
      employeeActivityItem.patientLastName = this.selectedPatient.lastName;
      employeeActivityItem.patientMiddleInitial = this.selectedPatient
        .middleInitial
        ? this.selectedPatient.middleInitial
        : "";
    }
    employeeActivityItem.tenantId = orgID;
    employeeActivityItem.timeZone = form.timeZone;
    employeeActivityItem.travelTime = this.travelTimeInMinutes;
    employeeActivityItem.updatedBy = userID.toString();
    employeeActivityItem.userFirstName = this.selectedUser.firstName;
    employeeActivityItem.userId = this.selectedUser.externalId.toString();
    employeeActivityItem.userLastName = this.selectedUser.lastName;
    employeeActivityItem.userMiddleInitial = this.selectedUser.middleInitial;
    employeeActivityItem.userName = this.selectedUser.userName;

    return employeeActivityItem;
  }

  cancel() {
    let alert = this.alertController.create({
      title: "Cancel Confirmation",
      message: "Are you sure you wish to cancel?",
      buttons: [
        {
          text: "No",
          handler: () => {},
        },
        {
          text: "Yes",
          handler: () => {
            if (this.employeeActivities.length > 0) {
              this.saveEmployeeActivities();
            }
            this.navCtrl.pop();
          },
        },
      ],
    });
    alert.present();
  }

  searchPatientInputChanged(e) {
    let searchTerm = e.target.value;
    this.patientSearchSubject.next(searchTerm);
  }

  searchUserInputChanged(e) {
    let searchTerm = e.target.value;
    this.userSearchSubject.next(searchTerm);
  }

  searchPatients(search: string): void {
    this.searchedPatients = [];
    this.getPatients(search);
  }

  searchUsers(search: string) {
    this.userOptions = [];
    this.resourceManagementService
      .getContactActivityLogSearch(search, true)
      .pipe()
      .subscribe((x) => {
        this.userOptions = x;
      });
  }

  get travelHoursInMinutes(): number {
    let travelHour = this.activityLogForm.get("travelHour").value
      ? this.activityLogForm.get("travelHour").value
      : 0;
    return travelHour * 60;
  }

  get travelMinutes(): number {
    return this.activityLogForm.get("travelMinute").value
      ? this.activityLogForm.get("travelMinute").value
      : 0;
  }

  get travelTimeInSeconds(): number {
    return (this.travelHoursInMinutes + this.travelMinutes) * 60;
  }

  get travelTimeInMinutes(): number {
    return this.travelHoursInMinutes + this.travelMinutes;
  }

  getPatients(search: string) {
    const options = ["On-Service"];

    options.forEach((option) => {
      this.patientChartService
        .getPatientsByParams(search, 0, 10, "lastName", "asc", "All")
        .pipe()
        .subscribe((x: ContentWrapper<ChartGridItem>) => {
          let a = [];

          x.content.forEach((x) => {
            a.push({
              id: x.externalId,
              firstName: x.firstName,
              lastName: x.lastName,
              displayName: `${x.firstName} ${x.lastName} - ${x.externalId}`,
            });
          });

          this.searchedPatients.push(...a);
          this.searchedPatients = [...this.searchedPatients];
        });
    });
  }

  // Hackish fix to show keyboard on IOS / Safari
  onClickPatient() {
    this.hiddenInput.setFocus();
    this.userListSelect.filterInput.nativeElement.focus();
  }
  onSelectHoliday() {
    if (this.activityLogForm.get("isHoliday").value) {
      this.selectedActivity = this.activityTypeOptions.find(
        (a) => a.display.toLowerCase() === "holiday"
      );
      this.activityLogForm.get("activityType").setValue(this.selectedActivity);
    } else {
      this.selectedActivity = "";
      this.activityLogForm.get("activityType").setValue(this.selectedActivity);
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/system-admin/activity-log-add/activity-log-add.ts